import type { WithId } from '@medplum/core';
import type { AllergyIntolerance, Coding } from '@medplum/fhirtypes';
import { IDENTIFIER_SYSTEMS, MEDICATION_CODE_SYSTEMS, SNOMED_SYSTEM } from './identifiers';

export type AllergyRecord = WithId<AllergyIntolerance>;

export type AllergyCriticality = NonNullable<AllergyIntolerance['criticality']>;

/** Clinical weight of a group, derived from every merged row because `criticality` is absent on most
 * imported rows: `high` when any row says so or any reaction is severe or names a serious
 * manifestation, `low` when the best evidence is low criticality or a mild reaction, otherwise
 * `unrecorded`, which sorts between the two. */
export type AllergySeverityTier = 'high' | 'unrecorded' | 'low';

/** One displayed allergy: the record shown for it plus every other row describing the same
 * substance (re-imports from other sites, other vocabularies for one ingredient, spelling variants). */
export type AllergyGroup = {
  /** Stable key: the displayed record's id. */
  key: string;
  record: AllergyRecord;
  merged: AllergyRecord[];
  /** Display name of the displayed record with source noise such as "(substance)" removed. */
  name: string;
  tier: AllergySeverityTier;
  /** Highest criticality across the group. */
  criticality: AllergyCriticality | undefined;
  /** Union of manifestations across the group, normalised and de-duplicated; placeholders such as
   * "Unknown" are dropped when a real manifestation exists. */
  reactions: string[];
  /** Distinct source systems that recorded the allergy, from the site identifiers on imported rows. */
  sources: number;
};

export type GroupedAllergies = {
  /** Highest severity tier first, then allergen name, then id. Rows with no renderable name, or a
   * placeholder name such as "Other" or an Epic category such as "Environmental", are left out. */
  groups: AllergyGroup[];
  /** Whether a "no known allergies" row is present. Such rows are a patient-level statement rather
   * than an allergy, so they are never listed. */
  noKnownAllergies: boolean;
};

const CODED_SYSTEMS: ReadonlySet<string> = new Set([MEDICATION_CODE_SYSTEMS.RXNORM, SNOMED_SYSTEM]);

/**
 * Word-bounded phrases that place a row under one substance when its names differ by more than
 * spelling: the venom of one insect under three names, a drug under its two generic names and a
 * brand, a class named in Latin and in English. Matched against the normalised names of a row.
 * Deliberately short: a class alias never absorbs the class's members ("opioid" does not match
 * "codeine"), so it only merges rows that name the class itself.
 */
export const ALLERGY_TEXT_ALIASES: Record<string, string[]> = {
  'bee-venom': ['bee venom', 'bee sting', 'honey bee', 'honeybee', 'apis mellifera'],
  dicyclomine: ['dicyclomine', 'dicycloverine', 'bentyl'],
  sulfonamide: ['sulfa', 'sulfonamide'],
  lactose: ['lactose'],
  nsaid: ['nsaid', 'non steroidal anti inflammatory', 'nonsteroidal anti inflammatory'],
  opioid: ['opioid', 'opiate'],
};

/** SNOMED CT concepts for "no known allergy" of any kind. */
const NO_KNOWN_ALLERGY_CODES: ReadonlySet<string> = new Set(['716186003', '409137002', '429625007', '428607008']);

const NO_KNOWN_ALLERGY_TEXT = [/\bno known\b.*\ballerg/, /\bnkd?a\b/, /\bno allerg/];

/** Names that say nothing about the substance: source placeholders, and the Epic allergen categories
 * that text-only imports carry in place of an allergen. Compared after {@link normalizeAllergenName}. */
const PLACEHOLDER_NAMES: ReadonlySet<string> = new Set([
  'other',
  'unknown',
  'none',
  'no information available',
  'not available',
  'unable to obtain',
  'see comment',
  'environmental',
  'food',
  'review food intolerance',
  'sugar protein starch',
]);

/** Salt and hydrate words that vary between exports of one ingredient. */
const SALT_WORDS: ReadonlySet<string> = new Set([
  'hcl',
  'hydrochloride',
  'hydrobromide',
  'phosphate',
  'sulfate',
  'sulphate',
  'sodium',
  'potassium',
  'calcium',
  'acetate',
  'maleate',
  'tartrate',
  'citrate',
  'mesylate',
  'besylate',
  'succinate',
  'bromide',
  'nitrate',
  'monohydrate',
  'dihydrate',
  'trihydrate',
  'anhydrous',
]);

const SERIOUS_REACTIONS = [
  'anaphylaxis',
  'anaphylactic',
  'angioedema',
  'stevens johnson',
  'sjs',
  'toxic epidermal necrolysis',
  'difficulty breathing',
  'trouble breathing',
  'shortness of breath',
  'dyspnea',
  'wheezing',
  'bronchospasm',
  'throat swelling',
  'throat tightness',
  'laryngeal edema',
  'airway',
  'hypotension',
  'loss of consciousness',
  'cardiac arrest',
  'respiratory distress',
];

/** Manifestations that only say a manifestation was not recorded. */
const NON_INFORMATIVE_REACTIONS: ReadonlySet<string> = new Set([
  'unknown',
  'other',
  'none',
  'not specified',
  'unspecified',
  'see comment',
  'see comments',
]);

/** Epic annotations appended to a manifestation's text. */
const REACTION_NOISE = /\s*\((reselect reaction|see comments?|explain in comments?)\)/gi;

const TRAILING_PARENTHETICAL = /\s*\([^()]*\)\s*$/;

const WORD_BOUNDARY = (phrase: string): RegExp =>
  new RegExp(`(^|[^a-z0-9])${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^a-z0-9])`);

const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

const singular = (word: string): string =>
  word.length > 3 && word.endsWith('s') && !/(ss|us|is)$/.test(word) ? word.slice(0, -1) : word;

/**
 * A name reduced to what identifies the substance: case-folded, parentheticals and punctuation
 * removed, salt words dropped when other words remain, plurals removed. "Tree Nuts (Food)",
 * "Tree nut (substance)" and "Tree Nut" all become "tree nut".
 */
export const normalizeAllergenName = (value: string): string => {
  const words = value
    .toLowerCase()
    .replace(/\([^()]*\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean);
  const withoutSalts = words.filter((word) => !SALT_WORDS.has(word));
  return (withoutSalts.length > 0 ? withoutSalts : words).map(singular).join(' ');
};

const firstCoding = (record: AllergyIntolerance): Coding | undefined => record.code?.coding?.[0];

const codedCoding = (record: AllergyIntolerance): Coding | undefined => {
  const coding = firstCoding(record);
  return coding?.system && coding.code && CODED_SYSTEMS.has(coding.system) ? coding : undefined;
};

/** The names a row carries for its substance: `code.text` and the first coding's display. Later
 * codings on imported rows are product and brand forms, which would join unrelated ingredients. */
const sourceNames = (record: AllergyIntolerance): string[] =>
  [record.code?.text, firstCoding(record)?.display].map((n) => collapseWhitespace(n ?? '')).filter(Boolean);

/** The substance name as the source wrote it, with trailing qualifiers such as "(substance)" or
 * "(Food Or Med)" removed. `undefined` when the row names nothing renderable. */
export const allergyDisplayName = (record: AllergyIntolerance): string | undefined => {
  const source = sourceNames(record)[0];
  if (!source) return undefined;
  let name = source;
  while (TRAILING_PARENTHETICAL.test(name)) {
    const stripped = name.replace(TRAILING_PARENTHETICAL, '').trim();
    if (!stripped) break;
    name = stripped;
  }
  return name;
};

/** Whether the row was ingested from an outside source rather than authored in Liza. */
export const isImportedAllergy = (record: AllergyIntolerance): boolean =>
  (record.identifier ?? []).some((id) => id.system === IDENTIFIER_SYSTEMS.EXTERNAL_ORIGINAL_ID);

const isNoKnownAllergy = (record: AllergyIntolerance): boolean => {
  const coding = firstCoding(record);
  if (coding?.system === SNOMED_SYSTEM && coding.code && NO_KNOWN_ALLERGY_CODES.has(coding.code)) return true;
  const text = sourceNames(record).join(' | ').toLowerCase();
  return NO_KNOWN_ALLERGY_TEXT.some((pattern) => pattern.test(text));
};

/** No name at all, or only placeholder names. Codings are not consulted, so a coded "Other" counts. */
const isPlaceholderName = (record: AllergyIntolerance): boolean => {
  const names = sourceNames(record).map(normalizeAllergenName).filter(Boolean);
  return names.length === 0 || names.every((name) => PLACEHOLDER_NAMES.has(name));
};

const titleCase = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const isShouting = (value: string): boolean => /[A-Z]/.test(value) && value === value.toUpperCase();

const manifestationText = (manifestation: { text?: string; coding?: Coding[] }): string | undefined =>
  collapseWhitespace(manifestation.text ?? manifestation.coding?.find((c) => c.display)?.display ?? '') || undefined;

/**
 * Manifestations across records, normalised and de-duplicated in order of first appearance. Epic
 * annotations are stripped, all-caps variants are folded into a cased one, and placeholders such
 * as "Unknown" are kept only when nothing informative was recorded.
 */
export const allergyReactions = (records: AllergyIntolerance[]): string[] => {
  const byKey = new Map<string, string>();
  for (const record of records) {
    for (const reaction of record.reaction ?? []) {
      for (const manifestation of reaction.manifestation ?? []) {
        const raw = manifestationText(manifestation);
        if (!raw) continue;
        const cleaned = collapseWhitespace(raw.replace(REACTION_NOISE, ''));
        if (!cleaned) continue;
        const key = cleaned.toLowerCase();
        const existing = byKey.get(key);
        if (existing === undefined || (isShouting(existing) && !isShouting(cleaned))) {
          byKey.set(key, isShouting(cleaned) ? titleCase(cleaned) : cleaned);
        }
      }
    }
  }
  const informative = [...byKey.entries()].filter(([key]) => !NON_INFORMATIVE_REACTIONS.has(key));
  return (informative.length > 0 ? informative : [...byKey.entries()]).map(([, display]) => display);
};

const CRITICALITY_RANK: Record<AllergyCriticality, number> = { high: 3, low: 2, 'unable-to-assess': 1 };

const criticalityRank = (record: AllergyIntolerance): number =>
  record.criticality ? CRITICALITY_RANK[record.criticality] : 0;

const highestCriticality = (records: AllergyIntolerance[]): AllergyCriticality | undefined =>
  records.reduce<AllergyCriticality | undefined>(
    (best, record) => (criticalityRank(record) > (best ? CRITICALITY_RANK[best] : 0) ? record.criticality : best),
    undefined,
  );

const namesSeriousReaction = (reactions: string[]): boolean =>
  reactions.some((reaction) => {
    const text = reaction.toLowerCase().replace(/[^a-z0-9]+/g, ' ');
    return SERIOUS_REACTIONS.some((serious) => WORD_BOUNDARY(serious).test(text));
  });

/** The severity tier for a set of rows describing one substance; see {@link AllergySeverityTier}.
 * Pass `reactions` when the caller already normalised them, so they are not computed twice. */
export const allergySeverityTier = (
  records: AllergyIntolerance[],
  reactions: string[] = allergyReactions(records),
): AllergySeverityTier => {
  const severities = records.flatMap((record) => (record.reaction ?? []).map((reaction) => reaction.severity));
  if (
    records.some((record) => record.criticality === 'high') ||
    severities.includes('severe') ||
    namesSeriousReaction(reactions)
  ) {
    return 'high';
  }
  if (records.some((record) => record.criticality === 'low') || severities.includes('mild')) return 'low';
  return 'unrecorded';
};

const parseInstant = (value: string | undefined): number => {
  if (!value) return 0;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? 0 : ms;
};

/** Most authoritative first: a Liza-authored row over an import of the same substance, then highest
 * criticality, then newest `recordedDate` and `lastUpdated`. Medplum ids are random, so `id` is only
 * the deterministic last resort. */
const byAuthority = (a: AllergyRecord, b: AllergyRecord): number => {
  const byOrigin = Number(isImportedAllergy(a)) - Number(isImportedAllergy(b));
  if (byOrigin !== 0) return byOrigin;
  const byCriticality = criticalityRank(b) - criticalityRank(a);
  if (byCriticality !== 0) return byCriticality;
  const byRecorded = parseInstant(b.recordedDate) - parseInstant(a.recordedDate);
  if (byRecorded !== 0) return byRecorded;
  const byUpdated = parseInstant(b.meta?.lastUpdated) - parseInstant(a.meta?.lastUpdated);
  if (byUpdated !== 0) return byUpdated;
  return a.id.localeCompare(b.id);
};

const TIER_ORDER: Record<AllergySeverityTier, number> = { high: 0, unrecorded: 1, low: 2 };

/** Highest severity tier first, then allergen name (case-folded), then id. */
export const byAllergySeverityThenName = (a: AllergyGroup, b: AllergyGroup): number => {
  const byTier = TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
  if (byTier !== 0) return byTier;
  const byName = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  if (byName !== 0) return byName;
  return a.record.id.localeCompare(b.record.id);
};

/** Every join key a row contributes: its first RxNorm or SNOMED code, each normalised name, and
 * any alias the names match. Rows sharing one key describe one substance. */
const joinKeys = (record: AllergyIntolerance): string[] => {
  const keys: string[] = [];
  const coding = codedCoding(record);
  if (coding) keys.push(`code:${coding.system}|${coding.code}`);
  const names = sourceNames(record).map(normalizeAllergenName).filter(Boolean);
  for (const name of names) keys.push(`name:${name}`);
  const text = names.join(' | ');
  for (const [alias, phrases] of Object.entries(ALLERGY_TEXT_ALIASES)) {
    if (phrases.some((phrase) => WORD_BOUNDARY(phrase).test(text))) keys.push(`alias:${alias}`);
  }
  return keys;
};

const sourceCount = (records: AllergyIntolerance[]): number =>
  new Set(
    records.flatMap((record) =>
      (record.identifier ?? [])
        .map((id) => id.system)
        .filter((system): system is string => Boolean(system) && system !== IDENTIFIER_SYSTEMS.EXTERNAL_ORIGINAL_ID),
    ),
  ).size;

const collapse = (records: AllergyRecord[]): AllergyGroup => {
  const sorted = [...records].sort(byAuthority);
  // Non-empty: a group is created with its first record.
  const record = sorted[0]!;
  const reactions = allergyReactions(sorted);
  return {
    key: record.id,
    record,
    merged: sorted.slice(1),
    name: allergyDisplayName(record) ?? '',
    tier: allergySeverityTier(sorted, reactions),
    criticality: highestCriticality(sorted),
    reactions,
    sources: sourceCount(sorted),
  };
};

/**
 * Group a patient's allergies into one line per substance.
 *
 * Imports describe one allergy as several rows: the same allergen from each source site, and the
 * same ingredient under a brand name, a generic name and a vocabulary class. Rows are joined when
 * they share a first RxNorm or SNOMED code, a normalised name, or an entry in
 * {@link ALLERGY_TEXT_ALIASES}; the join is transitive so a text-only brand row and a coded generic
 * row meet through the alias. Nothing merges on weaker signals, and a class row never absorbs its
 * members. Rows with no usable name are dropped: in production every such row is a bare proprietary
 * code, an "Other", or an Epic category, none of which a patient or clinician can act on.
 *
 * Pure and synchronous. Callers pass every record they want considered: a group built from part of
 * the list has the wrong reactions, tier and source count.
 */
export const groupAllergyRecords = (records: AllergyRecord[]): GroupedAllergies => {
  let noKnownAllergies = false;

  // Union-find over records: each join key points at the first record that carried it.
  const parent = new Map<string, string>();
  const find = (id: string): string => {
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root) ?? root;
    parent.set(id, root);
    return root;
  };
  const union = (a: string, b: string): void => {
    parent.set(find(a), find(b));
  };
  const owners = new Map<string, string>();

  const candidates: AllergyRecord[] = [];
  for (const record of records) {
    if (isNoKnownAllergy(record)) {
      noKnownAllergies = true;
      continue;
    }
    if (isPlaceholderName(record)) continue;
    candidates.push(record);
    parent.set(record.id, record.id);
    for (const key of joinKeys(record)) {
      const owner = owners.get(key);
      if (owner) {
        union(record.id, owner);
      } else {
        owners.set(key, record.id);
      }
    }
  }

  const members = new Map<string, AllergyRecord[]>();
  for (const record of candidates) {
    const root = find(record.id);
    const list = members.get(root);
    if (list) {
      list.push(record);
    } else {
      members.set(root, [record]);
    }
  }

  const groups = [...members.values()].map(collapse).sort(byAllergySeverityThenName);
  return { groups, noKnownAllergies };
};

/** Every record behind a group, displayed row and merged rows alike, for callers that still work on
 * individual resources (promotion, detail lookup by id). */
export const allergyGroupRecords = (group: AllergyGroup): AllergyRecord[] => [group.record, ...group.merged];
